/**
 * FAQ Widget
 * Displays product FAQs from metafield in compact card + modal format
 *
 * Features:
 * - Reads FAQs from product.metafields.custom.product_qa
 * - Compact card shows FAQ count with icon
 * - Modal displays full accordion with all Q&A
 * - Auto-hides if no FAQs available
 * - British English throughout
 * - All SVG icons inline
 * - Theme token styling
 *
 * @version 1.0.0
 */

(function(window) {
  'use strict';

  class FAQWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      // Widget state
      this.faqs = [];
      this.modalId = `faq-modal-${config.id}`;

      // Content (can be customised via block settings)
      this.content = {
        cardHeading: config.cardHeading || 'Questions & Answers',
        cardSubheading: config.cardSubheading || 'Everything you need to know',
        viewButton: config.viewButton || 'View FAQs',
        modalHeading: config.modalHeading || 'Frequently Asked Questions',
        modalSubheading: config.modalSubheading || 'Find answers to common questions about this product',
        noFaqsMessage: config.noFaqsMessage || 'No FAQs available for this product.'
      };
    }

    /**
     * Initialize widget - load FAQs from metafield
     */
    async init() {
      // Load FAQs from product metafield
      this.faqs = this.config.productFaqs || [];

      // Hide widget if no FAQs
      if (!this.faqs || this.faqs.length === 0) {
        this.hide();
        return;
      }
    }

    /**
     * Render widget to container
     */
    render() {
      // Don't render if no FAQs
      if (!this.faqs || this.faqs.length === 0) {
        this.hide();
        return;
      }

      // Build card + modal inline (x-modal pattern)
      const html = `
        ${this.buildCardHTML()}
        ${this.buildModalHTML()}
      `;

      // Use setContent() to properly handle skeleton loading
      this.setContent(html);

      // Attach event listeners
      this.attachEventListeners();

      this.emit('faq:rendered');
    }

    /**
     * Build widget card HTML
     */
    buildCardHTML() {
      const faqCount = this.faqs.length;

      return `
        <div class="pw-widget pw-faq-widget" style="width: 100%;">
          <div class="pw-widget--card pw-widget--card-clickable" style="padding: 16px; cursor: pointer;"
               data-modal-trigger="${this.modalId}">

            <!-- Header with Icon -->
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                ${this.getQuestionIconSVG()}
              </div>
              <div style="flex: 1;">
                <h3 class="pw-text-lg pw-font-bold" style="margin: 0;">${this.content.cardHeading}</h3>
                <p class="pw-text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.7);">
                  ${this.content.cardSubheading}
                </p>
              </div>
            </div>

            <!-- FAQ Count Badge -->
            <div style="margin-bottom: 16px;">
              <div style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; background: rgb(var(--color-base-button) / 0.1); border: 2px solid rgb(var(--color-base-button) / 0.3); border-radius: var(--rounded-button);">
                ${this.getCountIconSVG()}
                <span style="font-weight: 700; font-size: 0.95rem; color: rgb(var(--color-base-button));">
                  ${faqCount} ${faqCount === 1 ? 'Question' : 'Questions'}
                </span>
              </div>
            </div>

            <!-- Quick Preview (first 2 questions) -->
            <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px;">
              ${this.faqs.slice(0, 2).map(faq => `
                <div style="display: flex; align-items: start; gap: 8px;">
                  ${this.getCheckIconSVG()}
                  <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                    ${faq.question}
                  </span>
                </div>
              `).join('')}
              ${faqCount > 2 ? `
                <div style="display: flex; align-items: start; gap: 8px;">
                  <span class="pw-text-sm" style="color: rgb(var(--color-base-button)); font-weight: 600;">
                    + ${faqCount - 2} more ${faqCount - 2 === 1 ? 'question' : 'questions'}
                  </span>
                </div>
              ` : ''}
            </div>

            <!-- View FAQs Button -->
            <div style="display: flex; justify-content: flex-end;">
              <button
                type="button"
                class="pw-learn-more"
                aria-controls="${this.modalId}"
                aria-expanded="false"
              >
                <span>${this.content.viewButton}</span>
                <svg class="pw-learn-more__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Build modal HTML using x-modal pattern
     */
    buildModalHTML() {
      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Product FAQs" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">${this.content.cardHeading}</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 14" fill="none">
                    <path d="M15 1.00012L1 13.0001M1 1L15 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-base-button)); border-radius: var(--rounded-card); margin-bottom: 1rem;">
                    ${this.getQuestionIconSVG('var(--color-base-button-text)')}
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem;">
                    ${this.content.modalHeading}
                  </h3>
                  <p class="text-base text-opacity" style="max-width: 500px; margin: 0 auto;">
                    ${this.content.modalSubheading}
                  </p>
                </div>

                <!-- FAQ Accordion -->
                <div style="display: flex; flex-direction: column; gap: 0.75rem;">
                  ${this.faqs.map((faq, index) => this.buildFAQItem(faq, index)).join('')}
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Build individual FAQ accordion item
     */
    buildFAQItem(faq, index) {
      return `
        <details class="pw-faq-item" data-faq-index="${index}" style="background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card); overflow: hidden;">
          <summary class="pw-faq-summary" style="padding: 1rem 1.25rem; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 1rem; list-style: none;">
            <span style="font-size: 1rem; font-weight: 600; color: rgb(var(--color-drawer-text)); flex: 1;">
              ${faq.question}
            </span>
            <svg class="pw-faq-icon" style="width: 20px; height: 20px; color: rgb(var(--color-base-button)); transition: transform 0.2s ease; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </summary>
          <div class="pw-faq-answer" style="padding: 0 1.25rem 1rem 1.25rem; font-size: 0.9375rem; line-height: 1.7; color: rgb(var(--color-drawer-text) / 0.85);">
            ${faq.answer}
          </div>
        </details>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Make entire card clickable
      const cardElement = this.container.querySelector('[data-modal-trigger]');
      if (cardElement) {
        cardElement.addEventListener('click', (e) => {
          // Don't trigger if clicking the button directly
          if (e.target.closest('.pw-learn-more')) {
            return;
          }
          this.openModal();
        });
      }

      // Modal trigger button (still works if clicked directly)
      const learnMoreBtn = this.container.querySelector('.pw-learn-more');
      if (learnMoreBtn) {
        learnMoreBtn.addEventListener('click', () => this.openModal());
      }

      // Rotate icon when accordion opens
      const faqItems = this.container.querySelectorAll('.pw-faq-item');
      faqItems.forEach(item => {
        item.addEventListener('toggle', (e) => {
          const icon = item.querySelector('.pw-faq-icon');
          if (icon) {
            icon.style.transform = item.open ? 'rotate(180deg)' : 'rotate(0deg)';
          }
        });
      });
    }

    /**
     * Open modal
     */
    openModal() {
      const modal = document.getElementById(this.modalId);
      if (modal && modal.show) {
        modal.show();
      }
    }

    /**
     * SVG Icons
     */

    /**
     * Get question mark icon
     */
    getQuestionIconSVG(colourRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 20px; height: 20px; color: rgb(${colourRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Get count badge icon
     */
    getCountIconSVG() {
      return `
        <svg style="width: 16px; height: 16px; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
        </svg>
      `;
    }

    /**
     * Get check icon
     */
    getCheckIconSVG() {
      return `
        <svg style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }
  }

  // Register widget with ProductWidgets system
  if (window.ProductWidgets && window.ProductWidgets.widgets) {
    window.ProductWidgets.widgets.FAQWidget = FAQWidget;
  } else {
    console.error('[ProductWidgets] ProductWidgets system not available');
  }

})(window);
